import { CommonModule } from '@angular/common';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  NgZone,
  OnInit,
  ViewEncapsulation,
  inject,
  input,
  signal,
} from '@angular/core';
import { each as _each, escape, filter as _filter, find, forEach, includes, isEmpty, isNull, last as _last, map as _map, some } from 'lodash-es';
import { ParseService } from 'apps/account/src/account-shared/services/parse.service';
import { MessagingV1ApiService } from 'apps/account/src/account-shared/services/messaging-v1-api.service';
import { BookingEnginesV1ApiService } from 'apps/account/src/account-shared/services/booking-engines-v1-api.service';
import { RateCodesV1ApiService } from 'apps/account/src/account-shared/services/rate-codes-v1-api.service';
import { endpointUrl } from 'apps/account/src/account-shared/services/endpoint-url.util';
import { changesTransformer, transformType } from 'apps/account/src/account-shared/services/changes-transformer.util';
import { VersionSelectionService } from 'apps/account/src/account-shared/services/version-selection.service';
import { languageHelper } from 'apps/account/src/account-shared/services/language-helper.util';
import { IdentityService, SessionService } from 'account-hybrid/core/authentication';
import { SpacesService } from 'account-hybrid/common/components/spaces';
import { Environment, ENVIRONMENT_TOKEN } from 'account-hybrid/core/environment.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CnameUrlPipe, OnOffPipe } from '../contest-sheets/contest-sheets.pipes';
import { FieldEditorModalConfig, FtFieldEditorModalComponent } from '../contest-sheets/field-editor-modal.component';
import { computeDiffs, revertDiffs, snapshotClone } from '../contest-sheets/native-diff.util';
import { FormsModule } from '@angular/forms';
import { FtModalComponent } from '../../../common/components/modal/ft-modal.component';
import { FtModalChromeComponent } from '../../../common/components/modal/ft-modal-chrome.component';
import { FtSelectComponent } from './ft-select.component';
import { FtHtmlFieldComponent } from '../contest-sheets/ft-html-field.component';
import { ConfirmationDisplayTypeBridgePipe } from './touchpoint-sheets.pipes';
import { EditorsModule } from '../../../../account-shared/forms/editors/editors.module';

/**
 * v1 `insertionTypes` constant (core/enums/insertionType.js) — the photo-river integration
 * `insertionType` filter resolves the id against this table (loose `==`) and translates the
 * `description`. Inlined so the read-view label is computed natively via TranslateService.
 */
const INSERTION_TYPES: ReadonlyArray<{ id: string; description: string }> = [
  { id: '0', description: 'Appears before the element' },
  { id: '1', description: 'Appears inside the element as the first child' },
  { id: '2', description: 'Appears inside the element as the last child' },
  { id: '3', description: 'Appears after the element' },
];

/**
 * Native port of the AngularJS confirmation-touchpoint INTEGRATION editor (the heaviest tab).
 * Read-view (booking-engine codes + app-mode panels; Flip.to account code + excluded reservation
 * types) rendered verbatim; every edit modal is native over the native V1Api services. The
 * `integration` payload is passed in from the container; BookingEngines/RateCodes load in activate.
 */
@Component({
    selector: 'ft-touchpoint-integration-ng',
    imports: [
        CommonModule,
        FormsModule,
        TranslateModule,
        ConfirmationDisplayTypeBridgePipe,
        OnOffPipe,
        CnameUrlPipe,
        EditorsModule,
        FtFieldEditorModalComponent,
        FtModalComponent,
        FtModalChromeComponent,
        FtSelectComponent,
        FtHtmlFieldComponent,
    ],
    encapsulation: ViewEncapsulation.None,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    styleUrls: ['./ft-touchpoint-integration.component.scss'],
    templateUrl: './ft-touchpoint-integration.component.html',
})
export class FtTouchpointIntegrationComponent implements OnInit {
  readonly integration = input<any>();
  readonly layerName = input.required<string>();
  readonly touchpointType = input.required<string>();
  readonly touchpointGroupId = input.required<string>();
  readonly propertyUuid = input.required<string>();

  protected readonly model = signal<any>({ bookingEngines: [], rateCodes: [], engineNames: [], instruction: [], selectedEngine: {}, enums: {} });
  version: any = {};
  /** ftModal phase 2: the native field-editor modal request (null = closed) for the number fields. */
  protected readonly fieldEditorConfig = signal<FieldEditorModalConfig | null>(null);
  /** ftModal phase 2: open-time deep clone of model.integration for the native number diff-save + cancel-rollback. */
  private integrationSnapshot: any = null;

  /** ftModal phase 2 / WS3: the native confirmation RateCodes modal open flag. */
  protected readonly rateCodesModalOpen = signal(false);
  /** Modal-open shallow clone of model.rateCodes for the X/Cancel revert (v1 ft-snapshot="model.rateCodes"). */
  private rateCodesSnapshot: string[] | null = null;

  /** ftModal phase 2 / WS3: the native confirmation per-engine editEngineCodes modal open flag. */
  protected readonly engineCodesModalOpen = signal(false);
  /** Modal-open deep clone of model.bookingEngines for the diff-save commit + X/Cancel revert. */
  private bookingEnginesSnapshot: any = null;
  /** Stable id for the inline engine "is active" .ft-switch toggle. */
  protected readonly engineActiveUid = 'ft-integration-engine-active-switch';

  /** ftModal phase 2 / WS3: the native confirmation per-engine appmodes modal open flag. */
  protected readonly appmodesModalOpen = signal(false);
  /** Stable ids for the inline appmodes .ft-switch toggles. */
  protected readonly reopenerDesktopUid = 'ft-integration-reopener-desktop-switch';
  protected readonly reopenerMobileUid = 'ft-integration-reopener-mobile-switch';
  protected readonly timerEnabledUid = 'ft-integration-timer-enabled-switch';
  protected readonly incentiveUid = 'ft-integration-incentive-switch';

  /** WS5 Phase 3 #7: the native contestPage "Property code" (editPhotoIntegrationModal) codes modal open flag. */
  protected readonly editPhotoCodesModalOpen = signal(false);

  /** WS5 Phase 3 #7f: the native list+homepage "Profile image" (profileImageTypeModal) select modal open flag. */
  protected readonly profileImageModalOpen = signal(false);
  /** WS5 Phase 3 #7f: the native list+homepage codes (editCodesModal) list-editor modal open flag. */
  protected readonly editCodesModalOpen = signal(false);

  /** WS5 Phase 3 #7h: the native contestPage "Footer" (footerEditor) modal open flag. */
  protected readonly footerModalOpen = signal(false);
  /** Stable id for the footer ShowFooter inline .ft-switch toggle. */
  protected readonly footerSwitchUid = 'ft-integration-footer-active-switch';
  /** Stable [fields] reference for the footer FtHtmlField (avoids per-CD re-normalize). */
  protected readonly footerFields = ['footerContent'];
  /** Modal-open clone of model.languageItems for the footer X/Cancel revert (v1 dual ft-snapshot). */
  private languageItemsSnapshot: any = null;

  /** WS5 Phase 3 #7e: the native photo-river integration modal open flags (page-location / insertion-type / external-links). */
  protected readonly pageLocationModalOpen = signal(false);
  protected readonly insertionTypeModalOpen = signal(false);
  protected readonly externalLinksModalOpen = signal(false);
  /** Stable id for the photo-river external-links inline .ft-switch toggle. */
  protected readonly externalLinksSwitchUid = 'ft-integration-external-links-switch';

  /** landing-integration nav-pane screen (driven by model.integration.landingPageType). */
  protected readonly screenName = signal<'landing-url' | 'landing-custom' | 'landing-hosted'>('landing-hosted');

  private multiEngineMode = false;
  private bookingEngines: any[] = [];

  /** WS5 #10 inc 2x: which landing switch-to-X confirmation modal is open (1=integrated 2=custom 3=hosted). */
  protected readonly switchLandingModal = signal<1 | 2 | 3 | null>(null);

  // WS5 #10 inc 2x: the LAST $injector grabs drained — every v1 service here is native
  // (Integration = the v1 Messaging/Integration TWIN $http alias, 2w; the 'environment'
  // v1 provider is configure()d FROM ENVIRONMENT_TOKEN — the 2r-proven identity).
  private readonly integrationApi: any = inject(MessagingV1ApiService);
  private readonly bookingEnginesApi: any = inject(BookingEnginesV1ApiService);
  private readonly rateCodesApi: any = inject(RateCodesV1ApiService);
  private readonly endpointUrl: any = endpointUrl;
  private readonly changesTransformer: any = changesTransformer;
  private readonly transformType: any = transformType;
  private readonly versionSelection: any = inject(VersionSelectionService);
  private readonly identity: any = inject(IdentityService);
  private readonly spaces: any = inject(SpacesService);
  private readonly session: any = inject(SessionService);
  private readonly languageHelper: any = languageHelper;
  private readonly parse: any = inject(ParseService);
  private readonly environment: any = inject<Environment>(ENVIRONMENT_TOKEN);
  private readonly translateSvc = inject(TranslateService);
  private readonly translate: (key: string, params?: any) => string = (key: string, params?: any) => this.translateSvc.instant(key, params);
  private readonly zone = inject(NgZone);

  /** photo-river/homepage integration: the translated insertionType label (drives the ft-placeholder). */
  insertionTypeLabel(): string {
    const type = this.model().integration && this.model().integration.insertionType;
    const insertionType = INSERTION_TYPES.find((t) => t.id == type);
    return insertionType ? this.translate(insertionType.description) : '';
  }

  ngOnInit(): void {
    this.initModel();
    this.activate();
    // friend-landing: seed the nav-pane screen from landingPageType (v1 ftNavPaneItem `default`).
    if (this.touchpointType() === 'landing') {
      const t = this.integration() && this.integration().landingPageType;
      this.screenName.set(t == 1 ? 'landing-url' : t == 2 ? 'landing-custom' : 'landing-hosted');
    }
  }

  // WS5 #10 inc 2x: ngAfterViewInit/buildModals DELETED — the last bridged modals (the 3
  // landing switch-to-X confirmations) are native ft-modal-overlay blocks in the template;
  // every other touchpointType's modals went native in #7/#7e/#7f/#7h (the old buildModals
  // early-returns documented that per-type history).

  /**
   * Reproduces the v1 `ft-placeholder=""` on an ng-bind-html field: empty value → the directive
   * appends `<span class="placeholder">Click to edit</span>`. Used by the jsonSettings / landing
   * URL fields (empty on an unconfigured touchpoint → the "Click to edit" placeholder shows).
   */
  placeholderHtml(value: string | null | undefined): string {
    // Settings are literal text; only the empty-state wrapper is markup.
    if (value) return escape(value);
    return `<span class="placeholder">${escape(this.translate('flipto.account.ClickToEdit'))}</span>`;
  }

  /** friend-landing: per-language data bag (model.language[code]), guarded for the template. */
  langData(code: string): any {
    return (this.model().language && this.model().language[code]) || {};
  }

  /**
   * contestPage integration: the 'en' languageItem's footerContent (native port of the v1
   * `(model.languageItems | arrayItem:'code':'en').data.footerContent` — only shown when
   * isFooterActive, so the && in the template short-circuits when footer is off).
   */
  footerContentEn(): string {
    const en = (this.model().languageItems || []).find((l: any) => l.code === 'en');
    return (en && en.data && en.data.footerContent) || '';
  }

  // WS5 Phase 3 #7: contestPage integration "Book now" (staff-only bool) → native
  // FtFieldEditorModalComponent (type 'bool', the editQuotePlacePhoto recipe). Replaces the bridged
  // fieldsEditor.open. snapshot model.integration → Done commit(diffs); contestPage
  // permanentTransformType=8 (PhotoContestPageIntegration emits {isBookNowActive, isFooterActive,
  // codes}) → byte-identical to the v1 on-done="commit". X/Cancel → rollbackIntegration.
  editBookNow(): void {
    this.integrationSnapshot = snapshotClone(this.model().integration);
    this.fieldEditorConfig.set({
      type: 'bool',
      datasource: this.model().integration,
      fields: [{ name: 'isBookNowActive', label: this.translate('flipto.account.ShowBookNowButtonInNav') }],
      title: this.translate('flipto.account.BookNow'),
      onDone: () => this.commit(computeDiffs(this.integrationSnapshot, this.model().integration)),
      onCancel: () => this.rollbackIntegration(),
      onClose: () => {
        this.integrationSnapshot = null;
      },
    });
  }

  // --- read-view actions ---

  selectBookingEngine(engine: any): void {
    this.model().selectedEngine = engine;
  }

  showModal(id: string): void {
    // ftModal phase 2 / WS3: the RateCodes + per-engine editEngineCodes modals are now NATIVE
    // (ft-modal-overlay). Other modals (landing/photo-river/list/homepage/appmodes) stay bridged.
    if (id === 'integration.rateCodes') {
      this.openRateCodesModal();
      return;
    }
    if (id === 'confirmation.editEngineCodesModal') {
      this.openEngineCodesModal();
      return;
    }
    if (id === 'confirmation.appmodes') {
      this.openAppmodesModal();
      return;
    }
    if (id === 'editPhotoIntegrationModal') {
      this.openEditPhotoCodesModal();
      return;
    }
    if (id === 'profileImageTypeModal') {
      this.openProfileImageModal();
      return;
    }
    if (id === 'editCodesModal') {
      this.openEditCodesModal();
      return;
    }
    if (id === 'flipto.photo-explorer.integration.footerEditor') {
      this.openFooterModal();
      return;
    }
    if (id === 'photo.river.editPageLocationModal') {
      this.openPhotoRiverModal('pageLocation');
      return;
    }
    if (id === 'photo.river.editInsertionTypeModal') {
      this.openPhotoRiverModal('insertionType');
      return;
    }
    if (id === 'photo.river.editExternalLinksModal') {
      this.openPhotoRiverModal('externalLinks');
      return;
    }
    // WS5 #10 inc 2x: the 3 landing switch-to-X confirmation modals are NATIVE — the
    // last ftModal.show fall-through is gone (every modal id is handled above).
    if (id === 'flipto.prestay.landing.switch-to-integrated') {
      this.switchLandingModal.set(1);
      return;
    }
    if (id === 'flipto.prestay.landing.switch-to-custom') {
      this.switchLandingModal.set(2);
      return;
    }
    if (id === 'flipto.prestay.landing.switch-to-hosted') {
      this.switchLandingModal.set(3);
      return;
    }
  }

  /** The switch-to-X chrome Done — v1 ftModal done(): on-done=set<X>LandingPageType runs FIRST, then hide. */
  onSwitchLandingDone(): void {
    const type = this.switchLandingModal();
    if (type === 1) this.setIntegratedLandingPageType();
    else if (type === 2) this.setCustomLandingPageType();
    else if (type === 3) this.setHostedLandingPageType();
    this.switchLandingModal.set(null);
  }

  // ftModal phase 2 / WS3: open the native per-engine appmodes modal. selectBookingEngine() has set
  // model.selectedEngine. Snapshot model.bookingEngines for the diff-save commit + X/Cancel revert.
  openAppmodesModal(): void {
    this.bookingEnginesSnapshot = snapshotClone(this.model().bookingEngines);
    this.appmodesModalOpen.set(true);
  }

  /** Generic inline .ft-switch toggle (the appmodes reopener/timer/incentive switches; no side-effect). */
  onSwitchToggle(obj: any, prop: string, ev: Event): void {
    obj[prop] = (ev.target as HTMLInputElement).checked;
  }

  /** appmodes Done (v1 on-done="commit"): diff model.bookingEngines + commit, then close. */
  onAppmodesDone(): void {
    this.commit(computeDiffs(this.bookingEnginesSnapshot, this.model().bookingEngines));
    this.closeAppmodesModal();
  }

  /** appmodes X/Cancel (v1 ftModal snapshot.rollback): revert model.bookingEngines. */
  onAppmodesCancel(): void {
    if (this.bookingEnginesSnapshot) {
      revertDiffs(this.model().bookingEngines, this.bookingEnginesSnapshot, computeDiffs(this.bookingEnginesSnapshot, this.model().bookingEngines));
    }
    this.closeAppmodesModal();
  }

  private closeAppmodesModal(): void {
    this.appmodesModalOpen.set(false);
    this.bookingEnginesSnapshot = null;
  }

  // ftModal phase 2 / WS3: open the native per-engine editEngineCodes modal. selectBookingEngine()
  // has already set model.selectedEngine (one of model.bookingEngines). Snapshot model.bookingEngines
  // for the diff-save commit + X/Cancel revert.
  openEngineCodesModal(): void {
    this.model().selectedEngine.codes = this.model().selectedEngine.codes || { item: [] };
    this.bookingEnginesSnapshot = snapshotClone(this.model().bookingEngines);
    this.model().codeInModal = '';
    this.engineCodesModalOpen.set(true);
  }

  /** Engine "is active" toggle (v1 ft-switch on-change="updateBookingEngineState"): set + activate API. */
  onEngineActiveToggle(ev: Event): void {
    this.model().selectedEngine.isActive = (ev.target as HTMLInputElement).checked;
    this.updateBookingEngineState(this.model().selectedEngine);
  }

  /** + button / Enter: add model.codeInModal (raw, v1 ft-add-to-list="model.codeInModal"; map=code). */
  addEngineCodeFromButton(): void {
    this.addEngineCodeValue(this.model().codeInModal);
  }

  addEngineCodeFromEnter(ev: Event): void {
    ev.preventDefault();
    this.addEngineCodeValue(this.model().codeInModal);
  }

  /** v1 listEditor.addItem with map=code: trim-guard + unique dedup on {code} + push + clear. */
  private addEngineCodeValue(value: string): void {
    if (!value || !value.trim()) return;
    const items = this.model().selectedEngine.codes.item;
    if (find(items, { code: value })) return;
    items.push({ code: value });
    this.model().codeInModal = '';
  }

  /** v1 ft-removable removeItem(): splice the row out of model.selectedEngine.codes.item. */
  removeEngineCode(index: number): void {
    this.model().selectedEngine.codes.item.splice(index, 1);
  }

  /** editEngineCodes Done (v1 on-done="commit"): diff model.bookingEngines + commit, then close. */
  onEngineCodesDone(): void {
    this.commit(computeDiffs(this.bookingEnginesSnapshot, this.model().bookingEngines));
    this.closeEngineCodesModal();
  }

  /** editEngineCodes X/Cancel (v1 ftModal snapshot.rollback): revert model.bookingEngines. */
  onEngineCodesCancel(): void {
    if (this.bookingEnginesSnapshot) {
      revertDiffs(this.model().bookingEngines, this.bookingEnginesSnapshot, computeDiffs(this.bookingEnginesSnapshot, this.model().bookingEngines));
    }
    this.closeEngineCodesModal();
  }

  private closeEngineCodesModal(): void {
    this.engineCodesModalOpen.set(false);
    this.bookingEnginesSnapshot = null;
  }

  // WS5 Phase 3 #7: open the native contestPage "Property code" (editPhotoIntegrationModal) codes
  // modal. Replaces the bridged EDIT_PHOTO_CODES <ft-modal on-done="commit"
  // ft-snapshot="model.integration.codes"> compile. Snapshot model.integration (superset of the v1
  // codes snapshot — only codes mutate while this modal is open) for the diff-save commit + X/Cancel
  // revert; clear the add-input. contestPage permanentTransformType=8 → commit serializes TouchpointCode.
  openEditPhotoCodesModal(): void {
    this.model().integration.codes = this.model().integration.codes || { item: [] };
    this.integrationSnapshot = snapshotClone(this.model().integration);
    this.model().codesInModal = '';
    this.editPhotoCodesModalOpen.set(true);
  }

  /** + button / Enter: add model.codesInModal to model.integration.codes.item (map=code; unique). */
  addPhotoCodeFromButton(): void {
    this.addPhotoCodeValue(this.model().codesInModal);
  }

  addPhotoCodeFromEnter(ev: Event): void {
    ev.preventDefault();
    this.addPhotoCodeValue(this.model().codesInModal);
  }

  /** v1 listEditor.addItem with map=code: trim-guard + unique dedup on {code} + push + clear. */
  private addPhotoCodeValue(value: string): void {
    if (!value || !value.trim()) return;
    const items = this.model().integration.codes.item;
    if (find(items, { code: value })) return;
    items.push({ code: value });
    this.model().codesInModal = '';
  }

  /** v1 ft-removable removeItem(): splice the row out of model.integration.codes.item. */
  removePhotoCode(index: number): void {
    this.model().integration.codes.item.splice(index, 1);
  }

  /** editPhotoIntegrationModal Done (v1 on-done="commit"): diff model.integration + commit, then close. */
  onEditPhotoCodesDone(): void {
    this.commit(computeDiffs(this.integrationSnapshot, this.model().integration));
    this.closeEditPhotoCodesModal();
  }

  /** editPhotoIntegrationModal X/Cancel (v1 ftModal snapshot.rollback): revert model.integration. */
  onEditPhotoCodesCancel(): void {
    if (this.integrationSnapshot) {
      revertDiffs(this.model().integration, this.integrationSnapshot, computeDiffs(this.integrationSnapshot, this.model().integration));
    }
    this.closeEditPhotoCodesModal();
  }

  private closeEditPhotoCodesModal(): void {
    this.editPhotoCodesModalOpen.set(false);
    this.integrationSnapshot = null;
  }

  // WS5 Phase 3 #7f: open the native list/homepage "Profile image" (profileImageTypeModal) select
  // modal. Replaces the bridged PROFILE_IMAGE <ft-modal ft-snapshot="model.integration"
  // on-done="commit"> compile. Snapshot model.integration for the diff-save commit + X/Cancel revert;
  // the (change) on the ft-select-ng fires syncProfileImageType() (model.integration.isReplacePictures).
  openProfileImageModal(): void {
    this.integrationSnapshot = snapshotClone(this.model().integration);
    this.profileImageModalOpen.set(true);
  }

  /** profileImageTypeModal Done (v1 on-done="commit"): diff model.integration + commit, then close. */
  onProfileImageDone(): void {
    this.commit(computeDiffs(this.integrationSnapshot, this.model().integration));
    this.closeProfileImageModal();
  }

  /** profileImageTypeModal X/Cancel (v1 ftModal snapshot.rollback): revert model.integration. */
  onProfileImageCancel(): void {
    if (this.integrationSnapshot) {
      revertDiffs(this.model().integration, this.integrationSnapshot, computeDiffs(this.integrationSnapshot, this.model().integration));
    }
    this.closeProfileImageModal();
  }

  private closeProfileImageModal(): void {
    this.profileImageModalOpen.set(false);
    this.integrationSnapshot = null;
  }

  /** list/homepage editCodesModal header (v1 dynamic header-text by touchpointType). */
  codesModalHeader(): string {
    return this.touchpointType() === 'homepage'
      ? this.translate('flipto.account.platform.homepage.touchpoints.HomepageComponentCode')
      : this.translate('flipto.account.platform.quoteList.touchpoints.TouchpointCode');
  }

  // WS5 Phase 3 #7f: open the native list/homepage codes (editCodesModal) list-editor. Replaces the
  // bridged EDIT_CODES <ft-modal on-done="commit" ft-snapshot="model.integration"> compile.
  // onEditList/HomepageCodes already set model.tempTransformType=8; snapshot model.integration for the
  // diff-save commit + X/Cancel revert; clear the add-input. The add/remove reuse the generic
  // addPhotoCode*/removePhotoCode helpers (both operate on model.integration.codes.item + codesInModal).
  openEditCodesModal(): void {
    this.model().integration.codes = this.model().integration.codes || { item: [] };
    this.integrationSnapshot = snapshotClone(this.model().integration);
    this.model().codesInModal = '';
    this.editCodesModalOpen.set(true);
  }

  /** editCodesModal Done (v1 on-done="commit"): diff model.integration + commit (tempTransformType=8), then close. */
  onEditCodesDone(): void {
    this.commit(computeDiffs(this.integrationSnapshot, this.model().integration));
    this.closeEditCodesModal();
  }

  /** editCodesModal X/Cancel (v1 ftModal snapshot.rollback): revert model.integration. */
  onEditCodesCancel(): void {
    if (this.integrationSnapshot) {
      revertDiffs(this.model().integration, this.integrationSnapshot, computeDiffs(this.integrationSnapshot, this.model().integration));
    }
    this.closeEditCodesModal();
  }

  private closeEditCodesModal(): void {
    this.editCodesModalOpen.set(false);
    this.integrationSnapshot = null;
  }

  // WS5 Phase 3 #7h: open the native contestPage "Footer" (footerEditor) modal. Replaces the bridged
  // FOOTER_EDITOR <ft-modal on-done="commit" ft-snapshot="model.languageItems,
  // model.integration.isFooterActive"> compile. DUAL snapshot — model.languageItems (the footerContent
  // FtHtmlField edits) + model.integration (the isFooterActive switch) — both reverted on X/Cancel.
  openFooterModal(): void {
    this.languageItemsSnapshot = snapshotClone(this.model().languageItems);
    this.integrationSnapshot = snapshotClone(this.model().integration);
    this.footerModalOpen.set(true);
  }

  // footerEditor Done (v1 on-done="commit"): ONE merged PUT carrying BOTH the integration diffs
  // (transform 8 → isFooterActive + codes) AND the languageItems (footerContent) diffs. The
  // langItems diffs are PREFIXED with ['model','languageItems'] so commit()'s contestPage
  // langChanges filter+splice reproduces v1's changesTracker scope-rooted paths (WS5 #10 B0 #7h:
  // commit's MultiLanguageData strip expects the prefix; computeDiffs yields UNprefixed
  // [i,'data',field] — cf. commitLanguages). v1 sent both in one body; so do we.
  onFooterDone(): void {
    const integrationDiffs = computeDiffs(this.integrationSnapshot, this.model().integration);
    const langDiffs = computeDiffs(this.languageItemsSnapshot, this.model().languageItems).map((c: any) => {
      const copy = snapshotClone(c);
      copy.path = ['model', 'languageItems', ...copy.path];
      return copy;
    });
    this.commit([...integrationDiffs, ...langDiffs]);
    this.closeFooterModal();
  }

  /** footerEditor X/Cancel (v1 ftModal dual snapshot.rollback): revert languageItems + integration. */
  onFooterCancel(): void {
    if (this.languageItemsSnapshot) {
      revertDiffs(this.model().languageItems, this.languageItemsSnapshot, computeDiffs(this.languageItemsSnapshot, this.model().languageItems));
    }
    if (this.integrationSnapshot) {
      revertDiffs(this.model().integration, this.integrationSnapshot, computeDiffs(this.integrationSnapshot, this.model().integration));
    }
    this.closeFooterModal();
  }

  private closeFooterModal(): void {
    this.footerModalOpen.set(false);
    this.languageItemsSnapshot = null;
    this.integrationSnapshot = null;
  }

  // WS5 Phase 3 #7e: open a native photo-river integration modal. Replaces the bridged
  // PAGE_LOCATION / INSERTION_TYPE / EXTERNAL_LINKS <ft-modal on-done="commit"
  // ft-snapshot="model.integration"> compiles — each its own CDK overlay above the touchpoint sheet
  // (the bridged v1 ftModals at z3000 were occluded behind it). Snapshot model.integration for the
  // diff-save commit + X/Cancel revert (all 3 fields are flat on model.integration → FlatData).
  openPhotoRiverModal(which: 'pageLocation' | 'insertionType' | 'externalLinks'): void {
    this.integrationSnapshot = snapshotClone(this.model().integration);
    if (which === 'pageLocation') this.pageLocationModalOpen.set(true);
    else if (which === 'insertionType') this.insertionTypeModalOpen.set(true);
    else this.externalLinksModalOpen.set(true);
  }

  /** photo-river modal Done (v1 on-done="commit"): diff model.integration + commit, then close. */
  onPhotoRiverDone(): void {
    this.commit(computeDiffs(this.integrationSnapshot, this.model().integration));
    this.closePhotoRiverModals();
  }

  /** photo-river modal X/Cancel (v1 ftModal snapshot.rollback): revert model.integration. */
  onPhotoRiverCancel(): void {
    if (this.integrationSnapshot) {
      revertDiffs(this.model().integration, this.integrationSnapshot, computeDiffs(this.integrationSnapshot, this.model().integration));
    }
    this.closePhotoRiverModals();
  }

  private closePhotoRiverModals(): void {
    this.pageLocationModalOpen.set(false);
    this.insertionTypeModalOpen.set(false);
    this.externalLinksModalOpen.set(false);
    this.integrationSnapshot = null;
  }

  // ftModal phase 2 / WS3: open the native RateCodes modal. Snapshot model.rateCodes (shallow — a
  // string array) for X/Cancel + clear the add-input.
  openRateCodesModal(): void {
    this.rateCodesSnapshot = [...(this.model().rateCodes || [])];
    this.model().newRateCode = '';
    this.rateCodesModalOpen.set(true);
  }

  /** + button: add the UPPERCASED value (v1 ft-add-to-list="model.newRateCode.toUpperCase()"). */
  addRateCodeFromButton(): void {
    this.addRateCodeValue((this.model().newRateCode || '').toUpperCase());
  }

  /** Enter key: add the RAW value (v1 ft-add-to-list-by-enter, no expr → ngModel value verbatim). */
  addRateCodeFromEnter(ev: Event): void {
    ev.preventDefault();
    this.addRateCodeValue(this.model().newRateCode);
  }

  /** v1 listEditor.addItem(string) + ft-clear-after-add: trim-guard + unique dedup + push + clear. */
  private addRateCodeValue(value: string): void {
    if (!value || !value.trim()) return;
    if (this.model().rateCodes.indexOf(value) !== -1) return;
    this.model().rateCodes.push(value);
    this.model().newRateCode = '';
  }

  /** v1 ft-removable removeItem(): splice the row out of model.rateCodes. */
  removeRateCode(index: number): void {
    this.model().rateCodes.splice(index, 1);
  }

  /** RateCodes modal Done (v1 on-done="saveRateCodes"): persist directly, then close. */
  onRateCodesDone(): void {
    this.saveRateCodes();
    this.closeRateCodesModal();
  }

  /** RateCodes modal X/Cancel (v1 ftModal snapshot.rollback): restore model.rateCodes in place. */
  onRateCodesCancel(): void {
    if (this.rateCodesSnapshot) {
      this.model().rateCodes.splice(0, this.model().rateCodes.length, ...this.rateCodesSnapshot);
    }
    this.closeRateCodesModal();
  }

  private closeRateCodesModal(): void {
    this.rateCodesModalOpen.set(false);
    this.rateCodesSnapshot = null;
  }

  // --- quote "Post-stay Process" integration (edit-quote-integration.html, IntegrationController quote) ---

  isFliptoStaff(): boolean {
    return this.identity.isFliptoStaff();
  }

  /** v1 isEnablePhotosDisabled(): the isEnablePhotos panel is read-only on the un-upgradable versions. */
  isEnablePhotosDisabled(): boolean {
    const v = this.version || {};
    return (v.activatedVersion == 0 && v.currentVersion == 0) || (v.activatedVersion == 1 && v.currentVersion == 2);
  }

  private isNewVersion(): boolean {
    return !!(this.version && this.version.currentIsNewVersion && this.version.currentIsNewVersion());
  }

  // WS5 Phase 3 #7d: native bool field-editor (FtFieldEditorModalComponent → own CDK overlay above
  // the sheet), replacing the bridged fieldsEditor.open(). Same snapshot→commit(diffs)→rollback recipe
  // as editQuotePlacePhoto. The v1 modal had no description.
  editQuoteIsEnablePhotos(): void {
    if (this.isEnablePhotosDisabled()) return;
    this.integrationSnapshot = snapshotClone(this.model().integration);
    this.fieldEditorConfig.set({
      type: 'bool',
      datasource: this.model().integration,
      fields: [{ name: 'isEnablePhotos', label: 'Status' }],
      title: 'Invite guests to storyteller campaign',
      onDone: () => this.commit(computeDiffs(this.integrationSnapshot, this.model().integration)),
      onCancel: () => this.rollbackIntegration(),
      onClose: () => {
        this.integrationSnapshot = null;
      },
    });
  }

  // WS5 #10 Stage B B1b: the quote "Selector" editor → native FtFieldEditorModalComponent (type
  // 'text', SINGLE-object datasource model.integration → FtTextField's !isMultiLanguage branch).
  // Replaces the bridged fieldsEditor.open. onDone → commit(diffs) (FlatData → {selector}); X/Cancel
  // reverts. v1 translate-key title. isNewVersion() guard preserved. (Panel is *ngIf mode==='4'||'0'
  // — not rendered on quebec; reachability/save proven by _diag-b1-landing-selector-save.mjs.)
  editQuoteSelector(): void {
    if (this.isNewVersion()) return;
    this.integrationSnapshot = snapshotClone(this.model().integration);
    this.fieldEditorConfig.set({
      type: 'text',
      datasource: this.model().integration,
      fields: ['selector'],
      title: this.translate('flipto.account.platform.postStay.touchpoints.Integration.Selector'),
      onDone: () => this.commit(computeDiffs(this.integrationSnapshot, this.model().integration)),
      onCancel: () => this.rollbackIntegration(),
      onClose: () => {
        this.integrationSnapshot = null;
      },
    });
  }

  // WS5 #10 Stage B B1: the quote "External review URL" editor → native FtFieldEditorModalComponent
  // (type 'text', MULTI-LANGUAGE off model.languageItems — exact twin of editLandingPageUrl #7i).
  // Replaces the bridged fieldsEditor.open. onDone → commitLanguages(diffs) (byte-identical
  // {languages:[{languageCode, finalExternalReviewUrl}]} PUT); X/Cancel reverts languageItems.
  // v1 used the literal title 'External review URL' (not a translate key) + fieldsOptions{type:'url'}
  // (no required). The isNewVersion() guard is preserved verbatim (false on quebec → reachable).
  editQuoteExternalReviewUrl(): void {
    if (this.isNewVersion()) return;
    this.languageItemsSnapshot = snapshotClone(this.model().languageItems);
    this.fieldEditorConfig.set({
      type: 'text',
      datasource: this.model().languageItems,
      fields: ['finalExternalReviewUrl'],
      fieldsOptions: { type: 'url' },
      title: 'External review URL',
      onDone: () => this.commitLanguages(computeDiffs(this.languageItemsSnapshot, this.model().languageItems)),
      onCancel: () => this.rollbackLanguageItems(),
      onClose: () => {
        this.languageItemsSnapshot = null;
      },
    });
  }

  // WS5 #10 Stage B B1: the quote "External review for low rating" BOOL editor → native
  // FtFieldEditorModalComponent (exact twin of editQuotePlacePhoto/editQuoteIsEnablePhotos).
  // Replaces the bridged fieldsEditor.open. onDone → commit(diffs) (FlatData on model.integration);
  // X/Cancel reverts. Title is the v1 translate key. isNewVersion() guard preserved verbatim.
  editQuoteIsLowRatingExternalReview(): void {
    if (this.isNewVersion()) return;
    this.integrationSnapshot = snapshotClone(this.model().integration);
    this.fieldEditorConfig.set({
      type: 'bool',
      datasource: this.model().integration,
      fields: [{ name: 'isLowRatingExternalReview', label: 'Status' }],
      title: this.translate('flipto.account.platform.postStay.touchpoints.Integration.IsLowRatingExternalReview'),
      onDone: () => this.commit(computeDiffs(this.integrationSnapshot, this.model().integration)),
      onCancel: () => this.rollbackIntegration(),
      onClose: () => {
        this.integrationSnapshot = null;
      },
    });
  }

  // ftModal phase 2 #5: the 2 quote-integration BOOL place-info editors → native shell (staff-only
  // rows, reachable on quebec's quote/Post-stay-Process integration tab). Same recipe as the number
  // fields: snapshot model.integration → native ft-bool-field-ng mutates in place → Done diffs
  // live-vs-snapshot → this.commit(diffs) (FlatData) VERBATIM; Cancel reverts.
  editQuotePlacePhoto(): void {
    this.integrationSnapshot = snapshotClone(this.model().integration);
    this.fieldEditorConfig.set({
      type: 'bool',
      datasource: this.model().integration,
      fields: this.model().fieldsForPlacePhoto,
      description: 'Ask your storytellers where their photo was taken.',
      title: 'Request place info for photo',
      onDone: () => this.commit(computeDiffs(this.integrationSnapshot, this.model().integration)),
      onCancel: () => this.rollbackIntegration(),
      onClose: () => {
        this.integrationSnapshot = null;
      },
    });
  }

  editQuotePlaceQuote(): void {
    this.integrationSnapshot = snapshotClone(this.model().integration);
    this.fieldEditorConfig.set({
      type: 'bool',
      datasource: this.model().integration,
      fields: this.model().fieldsForPlaceQuote,
      description: 'Ask your storytellers what place is related to their quote.',
      title: 'Request place info for quote',
      onDone: () => this.commit(computeDiffs(this.integrationSnapshot, this.model().integration)),
      onCancel: () => this.rollbackIntegration(),
      onClose: () => {
        this.integrationSnapshot = null;
      },
    });
  }

  /** v1 (save)="model.integration.allowedPlacesType = $event; commit([{...}])". */
  onAllowedPlacesSave(value: any): void {
    this.model().integration.allowedPlacesType = value;
    this.commit([{ kind: 'E', lhs: null, rhs: value, path: ['allowedPlacesType'] }]);
  }

  // --- bridged save handlers (delegated by the modals via FieldsEditorBridgeService) ---

  commit(deepDiffChanges: any[]): void {
    const fixedChanges = _map(deepDiffChanges, (change: any) => {
      // v1 angular.copy stripped $$hashKey recursively; snapshotClone doesn't. Safe here:
      // every reachable change lhs/rhs is a primitive or a freshly-built object no
      // ng-repeat ever tagged (native *ngFor doesn't tag; the 2u ngExtend strips the one
      // object-merge path inside the transformer).
      const copy = snapshotClone(change);
      if (change.path.join('.').indexOf('model.bookingEngines') !== -1 || change.path.join('.').indexOf('model.languageItems') !== -1) {
        copy.path.splice(0, 2);
      }
      return copy;
    });
    const changes = this.multiEngineMode
      ? this.changesTransformer(this.transformType.BookingEngineIntegrationData, fixedChanges, this.bookingEngines)
      : this.changesTransformer(this.model().tempTransformType || this.model().permanentTransformType || this.transformType.FlatData, fixedChanges, this.integration());
    if (changes.maxQuoteTextLength !== undefined && isNull(changes.maxQuoteTextLength)) {
      changes.maxQuoteTextLength = 0;
    }
    const url = this.endpointUrl.getIntegrationUrl(this.layerName(), this.touchpointType(), this.touchpointGroupId(), this.spaces.getPropertyUuid());

    // WS5 #10 Stage B B0 (#7h): v1 integration.controller.js:201-215 — the contestPage-only
    // langChanges branch. Transform 8 (PhotoContestPageIntegration) emits only
    // {isBookNowActive, isFooterActive, codes} and does NOT serialize footerContent, so v1 ALSO
    // ran the model.languageItems diffs through MultiLanguageData and appended them as
    // changes.languages. GATE ON touchpointType, NEVER on transform-8 — the list/homepage codes
    // modals also set tempTransformType=8 and must NOT receive a languages key. onFooterDone
    // supplies the 'model.languageItems'-PREFIXED diffs this filter+splice expects (the
    // commitLanguages pattern). The aliased footerContent change inside the integration diff
    // (path 'languages.item...') matches neither this filter nor transform 8 → no double-serialize.
    if (this.touchpointType() === 'contestPage') {
      const langChanges = _filter(deepDiffChanges, (change: any) => change.path.join('.').indexOf('model.languageItems') !== -1);
      const fixedLangChanges = _map(langChanges, (change: any) => {
        const copy = snapshotClone(change);
        if (change.path.join('.').indexOf('model.languageItems') !== -1) {
          copy.path.splice(0, 2);
        }
        return copy;
      });
      const transformedLanguages = this.changesTransformer(this.transformType.MultiLanguageData, fixedLangChanges, this.integration().languages.item);
      if (transformedLanguages.length > 0) {
        changes.languages = transformedLanguages;
      }
    }

    !isEmpty(changes) && this.integrationApi.put(url, changes);
    this.model().tempTransformType = undefined;
  }

  updateBookingEngineState(engine: any): void {
    this.bookingEnginesApi.activate(this.spaces.getPropertyUuid(), engine);
  }

  saveRateCodes(): void {
    this.rateCodesApi.updateRateCodes(this.spaces.getPropertyUuid(), this.model().rateCodes);
  }

  // --- friend LANDING integration handlers (integration.controller.js landing path) ---

  // The 4 fields-editor panels open the bridged ftModalFieldsEditor. URL panels save via
  // commitLanguages (languageItems); jsonSettings panels save via commit (integration).
  // WS5 Phase 3 #7i: the landing-url-screen "Landing page url" editor → native
  // FtFieldEditorModalComponent (type 'text', MULTI-LANGUAGE — FtTextField renders one input per
  // language off the model.languageItems array). Replaces the bridged fieldsEditor.open. onDone →
  // commitLanguages(diffs): computeDiffs(snapshot, model.languageItems) yields [i,'data',field] paths
  // (NO 'model.languageItems' prefix → the strip is skipped) which MultiLanguageDataTransformer reads
  // as path[0]=index + last=field → byte-identical {languages:[{languageCode, landingPageUrl}]} PUT.
  // X/Cancel reverts the languageItems edits (the diff-save path bypasses the shell's snapshot).
  editLandingPageUrl(): void {
    this.languageItemsSnapshot = snapshotClone(this.model().languageItems);
    this.fieldEditorConfig.set({
      type: 'text',
      datasource: this.model().languageItems,
      fields: ['landingPageUrl'],
      fieldsOptions: { type: 'url', required: true },
      title: 'Landing page URL',
      onDone: () => this.commitLanguages(computeDiffs(this.languageItemsSnapshot, this.model().languageItems)),
      onCancel: () => this.rollbackLanguageItems(),
      onClose: () => {
        this.languageItemsSnapshot = null;
      },
    });
  }

  /** Cancel revert for the multi-language (languageItems) field editors (v1 ftModal snapshot.rollback). */
  private rollbackLanguageItems(): void {
    if (!this.languageItemsSnapshot) return;
    revertDiffs(this.model().languageItems, this.languageItemsSnapshot, computeDiffs(this.languageItemsSnapshot, this.model().languageItems));
  }

  // WS5 Phase 3 #7c: the landing-url-screen JsonSettings (ACE js) editor → native
  // FtFieldEditorModalComponent (type 'js'), twin of editListJsonSettings. Replaces the bridged
  // fieldsEditor.open. Flat model.integration field (jsonSettings); onDone 'commit' (no transform set
  // → FlatData), so snapshot → commit(diffs) → rollbackIntegration matches v1 byte-for-byte.
  editLandingJsonSettings(): void {
    this.integrationSnapshot = snapshotClone(this.model().integration);
    this.fieldEditorConfig.set({
      type: 'js',
      datasource: this.model().integration,
      fields: [{ name: 'jsonSettings' }],
      title: this.translate('flipto.account.platform.preStay.touchpoints.JsonSettings'),
      onDone: () => this.commit(computeDiffs(this.integrationSnapshot, this.model().integration)),
      onCancel: () => this.rollbackIntegration(),
      onClose: () => {
        this.integrationSnapshot = null;
      },
    });
  }

  // WS5 Phase 3 #7c: the landing-custom-screen JsonSettings (jsonCustomSettings, ACE js) editor →
  // native FtFieldEditorModalComponent (type 'js'). Same recipe as editLandingJsonSettings (flat
  // model.integration field, commit/FlatData).
  editLandingJsonCustomSettings(): void {
    this.integrationSnapshot = snapshotClone(this.model().integration);
    this.fieldEditorConfig.set({
      type: 'js',
      datasource: this.model().integration,
      fields: [{ name: 'jsonCustomSettings' }],
      title: this.translate('flipto.account.platform.preStay.touchpoints.JsonSettings'),
      onDone: () => this.commit(computeDiffs(this.integrationSnapshot, this.model().integration)),
      onCancel: () => this.rollbackIntegration(),
      onClose: () => {
        this.integrationSnapshot = null;
      },
    });
  }

  // WS5 #10 Stage B B1b: the landing "Redirect URL" editor → native FtFieldEditorModalComponent
  // (type 'text', MULTI-LANGUAGE off model.languageItems — exact twin of editLandingPageUrl).
  // Replaces the bridged fieldsEditor.open. onDone → commitLanguages(diffs) → byte-identical
  // {languages:[{languageCode, postSignupRedirectUrl}]} PUT; X/Cancel reverts languageItems. v1
  // literal title 'Redirect URL', fieldsOptions {type:'url', required:true}, NO isNewVersion guard.
  // (Row is [hidden] at landingPageType==3 — not reachable on quebec; save proven off-gate.)
  editLandingRedirectUrl(): void {
    this.languageItemsSnapshot = snapshotClone(this.model().languageItems);
    this.fieldEditorConfig.set({
      type: 'text',
      datasource: this.model().languageItems,
      fields: ['postSignupRedirectUrl'],
      fieldsOptions: { type: 'url', required: true },
      title: 'Redirect URL',
      onDone: () => this.commitLanguages(computeDiffs(this.languageItemsSnapshot, this.model().languageItems)),
      onCancel: () => this.rollbackLanguageItems(),
      onClose: () => {
        this.languageItemsSnapshot = null;
      },
    });
  }

  // WS5 Phase 3 #7i: the per-language custom landing-page editor (textarea, size xl) → native
  // FtFieldEditorModalComponent (type 'textarea', the new FtTextareaField). Replaces the bridged
  // fieldsEditor.open. SINGLE-object datasource (model.language[code]) → the shell's own per-field
  // snapshot rollback handles X/Cancel (v1 snapshot="model.language.code"); onDone →
  // commitLandingPageHtml() (the whole-language PUT, no diff — v1 on-done="commitLandingPageHtml").
  editLandingPageHtml(languageCode: string): void {
    this.model().selectedLanguage = languageCode;
    // v1 edit-landing-integration.html has two trigger variants: languages.length == 1 →
    // CustomizedPage/CustomizedPageDescription; > 1 → CustomizedPageFor/…DescriptionFor
    // interpolated with the language name.
    const languages = this.model().languages || [];
    const multi = languages.length > 1;
    const name = languages.find((l: any) => l.code === languageCode)?.name;
    this.fieldEditorConfig.set({
      type: 'textarea',
      size: 'xl',
      fieldsOptions: { height: 500 },
      datasource: this.model().language[languageCode],
      fields: ['landingPageHtml'],
      title: multi
        ? this.translate('flipto.account.CustomizedPageFor', { name })
        : this.translate('flipto.account.CustomizedPage'),
      description: multi
        ? this.translate('flipto.account.CustomizedPageDescriptionFor', { name })
        : this.translate('flipto.account.CustomizedPageDescription'),
      onDone: () => this.commitLandingPageHtml(),
    });
  }

  commitLandingPageHtml(): void {
    const model = {
      propertyUuid: this.spaces.getPropertyUuid(),
      landingPageType: this.model().integration.landingPageType,
      // v1 angular.extend shallow-merge. NOT mechanically identical (extend restores the
      // destination's $$hashKey; Object.assign would copy a source one) — safe HERE because
      // model.language[code] is a language.data object that no ng-repeat ever tags on
      // either host (v1 repeats iterate languageItems/{code,name} clones, not .data).
      languages: [Object.assign({ languageCode: this.model().selectedLanguage }, this.model().language[this.model().selectedLanguage])],
    };
    const url = this.endpointUrl.getIntegrationUrl(this.layerName(), this.touchpointType(), this.touchpointGroupId(), this.spaces.getPropertyUuid());
    this.integrationApi.put(url, model);
  }

  commitLanguages(deepDiffChanges: any[]): void {
    const fixedChanges = _map(deepDiffChanges, (change: any) => {
      const copy = snapshotClone(change); // v1 angular.copy of a plain deep-diff change object
      if (change.path.join('.').indexOf('model.languageItems') !== -1) {
        copy.path.splice(0, 2);
      }
      return copy;
    });
    const changes = { languages: this.changesTransformer(this.transformType.MultiLanguageData, fixedChanges, this.integration().languages.item) };
    const url = this.endpointUrl.getIntegrationUrl(this.layerName(), this.touchpointType(), this.touchpointGroupId(), this.spaces.getPropertyUuid());
    !isEmpty(changes.languages) && this.integrationApi.put(url, changes);
  }

  allowToggleLandingPageType(landingPageType: number): boolean {
    switch (landingPageType) {
      case 1:
        return some(this.model().languageItems, (lang: any) => !!(lang.data && lang.data.landingPageUrl));
      case 2:
        return some(this.model().languageItems, (lang: any) => !!(lang.data && lang.data.landingPageHtml));
      case 3:
        return true;
      default:
        return false;
    }
  }

  setIntegratedLandingPageType(): void {
    this.toggleLandingPageType(1);
  }

  setCustomLandingPageType(): void {
    this.toggleLandingPageType(2);
  }

  setHostedLandingPageType(): void {
    this.toggleLandingPageType(3);
  }

  toggleLandingPageType(type: number): void {
    this.model().integration.landingPageType = type;
    const url = this.endpointUrl.getIntegrationUrl(this.layerName(), this.touchpointType(), this.touchpointGroupId(), this.spaces.getPropertyUuid());
    this.integrationApi.put(url, { landingPageType: type });
  }

  getLandingPagePreviewUrl(pageType: string): string {
    const promoCode = this.model().languageItems[0].data.promoterCode;
    return this.environment.FliptoPlatformDomain + '/' + promoCode + '?pageType=' + pageType;
  }

  // --- QUOTE-LIST "Quotes List" integration handlers (edit-list-integration.html) ---

  // ftModal phase 2 #4: native NUMBER field + native deep-diff save (no bridged ftModal → no
  // CDK-overlay occlusion). Snapshot model.integration at open; ft-number-field-ng mutates it in
  // place; Done diffs live-vs-snapshot and reuses `this.commit(diffs)` VERBATIM — the FlatData
  // transformer reads only `_last(change.path)` so a native diff rooted at model.integration
  // (path ['pageSize']) flows through commit's existing splice/transform identically to the v1
  // changesTracker diffs (model.integration === this.integration()). Cancel reverts.
  editListPageSize(): void {
    this.integrationSnapshot = snapshotClone(this.model().integration);
    this.fieldEditorConfig.set({
      type: 'number',
      datasource: this.model().integration,
      fields: [{ name: 'pageSize' }],
      fieldsOptions: { min: 1, required: true },
      title: this.translate('flipto.account.platform.quoteList.touchpoints.NumberOfQuotesToFeature'),
      onDone: () => this.commit(computeDiffs(this.integrationSnapshot, this.model().integration)),
      onCancel: () => this.rollbackIntegration(),
      onClose: () => {
        this.integrationSnapshot = null;
      },
    });
  }

  editListMaxQuoteLength(): void {
    this.integrationSnapshot = snapshotClone(this.model().integration);
    this.fieldEditorConfig.set({
      type: 'number',
      datasource: this.model().integration,
      fields: [{ name: 'maxQuoteTextLength' }],
      fieldsOptions: { min: 1, max: 255, required: true },
      title: this.translate('flipto.account.platform.quoteList.touchpoints.QuoteCharacterLimit'),
      onDone: () => this.commit(computeDiffs(this.integrationSnapshot, this.model().integration)),
      onCancel: () => this.rollbackIntegration(),
      onClose: () => {
        this.integrationSnapshot = null;
      },
    });
  }

  /** Cancel: revert the native number field's in-place edits (v1 tracker.rollback → DeepDiff.revertChange). */
  private rollbackIntegration(): void {
    if (!this.integrationSnapshot) return;
    revertDiffs(this.model().integration, this.integrationSnapshot, computeDiffs(this.integrationSnapshot, this.model().integration));
  }

  // ftModal phase 2 #11: the quote-list JSON (ACE js) editor → native FtCodeField. Same commit-diff
  // recipe as the number fields (snapshot model.integration → ACE mutates → Done commit(diffs) FlatData).
  editListJsonSettings(): void {
    this.integrationSnapshot = snapshotClone(this.model().integration);
    this.fieldEditorConfig.set({
      type: 'js',
      datasource: this.model().integration,
      fields: [{ name: 'jsonSettings' }],
      title: this.translate('flipto.account.platform.quoteList.touchpoints.Json'),
      onDone: () => this.commit(computeDiffs(this.integrationSnapshot, this.model().integration)),
      onCancel: () => this.rollbackIntegration(),
      onClose: () => {
        this.integrationSnapshot = null;
      },
    });
  }

  // v1: ng-click="model.tempTransformType = 8" + ft-targets-modal="editCodesModal".
  onEditListCodes(): void {
    this.model().tempTransformType = 8;
    this.showModal('editCodesModal');
  }

  // --- HOMEPAGE "Homepage Component" integration handlers (edit-homepage-integration.html) ---

  onEditHomepageCodes(): void {
    this.model().tempTransformType = 8;
    this.showModal('editCodesModal');
  }

  // WS5 Phase 3 #7f: the homepage "Advanced properties" JSON (ACE js) editor → native
  // FtFieldEditorModalComponent (type 'js'), twin of editListJsonSettings. Replaces the bridged
  // editHomepageIntegrationModal <ft-modal><ft-js-field datasource="model.integration"
  // fields="['jsonSettings']"> compile. v1 sets tempTransformType=1 before opening → commit serializes
  // with transform 1; snapshot model.integration → Done commit(diffs); Cancel rollbackIntegration.
  onEditHomepageJson(): void {
    this.model().tempTransformType = 1;
    this.integrationSnapshot = snapshotClone(this.model().integration);
    this.fieldEditorConfig.set({
      type: 'js',
      datasource: this.model().integration,
      fields: [{ name: 'jsonSettings' }],
      title: this.translate('flipto.account.platform.homepage.touchpoints.AdvancedProperties'),
      onDone: () => this.commit(computeDiffs(this.integrationSnapshot, this.model().integration)),
      onCancel: () => this.rollbackIntegration(),
      onClose: () => {
        this.integrationSnapshot = null;
      },
    });
  }

  // --- model setup (ported from IntegrationController, confirmation path) ---

  private initModel(): void {
    const integration = this.integration();
    this.multiEngineMode = Object.prototype.hasOwnProperty.call(integration, 'bookingEngines');
    if (!(integration.codes && integration.codes.item)) {
      integration.codes = { item: [] };
    }
    // Inlined byte-exact from the v1 core/enums/*.js + the read-view copy in
    // touchpoint-sheets.pipes.ts (CONFIRMATION_DISPLAY_TYPES) — static option arrays
    // bound to <ft-select> dropdowns; drained off the INJECTOR_TOKEN bridge.
    const confirmationDisplayTypes = [
      { id: '1', name: 'Integrated - basic sharing component attached to an element ' },
      { id: '2', name: 'Fullscreen - basic sharing component overlaid on page' },
      { id: '3', name: 'Immersive - a complete personalized guest experience (New)' },
    ];
    const confirmationTimerActionTypes = [
      { id: '0', name: 'Proceed to main view after timer expires' },
      { id: '1', name: 'Return to confirmation page after timer expires' },
    ];
    const insertionTypes = [
      { id: '0', name: 'Before Begin', description: 'Appears before the element' },
      { id: '1', name: 'After Begin', description: 'Appears inside the element as the first child' },
      { id: '2', name: 'Before End', description: 'Appears inside the element as the last child' },
      { id: '3', name: 'After End', description: 'Appears after the element' },
    ];
    const overlayTypes = [
      { id: '0', name: 'BottomBanner', description: 'Along the bottom of the screen' },
      { id: '1', name: 'BottomRightCorner', description: 'Positioned at the bottom right corner' },
      { id: '2', name: 'BottomLeftCorner', description: 'Positioned at the bottom left corner' },
    ];

    this.model.set({
      integration,
      allowedPlacesTypes: [{ value: 0, label: 'All' }],
      placesCount: 0,
      selectedEngine: {},
      emails: '',
      isActive: integration.isActive == '1',
      instruction: [],
      engineNames: [],
      postStayVersions: [],
      tempTransformType: undefined,
      permanentTransformType: undefined,
      property: this.session.getProperty(this.spaces.getPropertyUuid()),
      rateCodes: [],
      bookingEngines: [],
      enums: { confirmationTimerActionTypes, confirmationDisplayTypes, insertionTypes, overlayTypes },
    });

    if (this.multiEngineMode) {
      this.model().bookingEngines = this.bookingEngines = integration.bookingEngines.item;
      forEach(this.bookingEngines, (bookingEngine: any) => {
        bookingEngine.codes = bookingEngine.codes || { item: [] };
      });
    }

    // homepage / list: the ProfileImage select (UseGuestPhoto / ReplaceGuestPhoto) — v1
    // IntegrationController homepage/list branch. The native profileImageTypeName() getter +
    // syncProfileImageType() replace the v1 $watch (keeps integration.isReplacePictures in sync
    // with the selected id so the bridged ft-select modal's change persists on commit).
    if (this.touchpointType() === 'homepage' || this.touchpointType() === 'list') {
      integration.isReplacePictures = integration.isReplacePictures === '1';
      const prefix = 'flipto.account.platform.touchpoints.';
      this.model().profileImageTypes = [
        { id: 0, name: this.translate(prefix + 'UseGuestPhoto') },
        { id: 1, name: this.translate(prefix + 'ReplaceGuestPhoto') },
      ];
      this.model().selectedImageProfileType = { id: integration.isReplacePictures ? 1 : 0 };
      this.model().selectedImageProfileType.name = this.profileImageTypeName();
    }

    // quote "Post-stay Process" integration (IntegrationController quote branch): parse the
    // place-info/allowedPlaces flags to their typed values + seed integrationModes / fieldsForPlace*.
    // The v1 $watches that dynamically rebuild fieldsForPlace*/allowedPlacesTypes only fire when the
    // user toggles in the (un-gated) edit modal; the read-view at load reflects this initial state.
    if (this.touchpointType() === 'quote') {
      const t = this.translate;
      this.model().integrationModes = [
        { mode: '1', name: t('flipto.account.platform.postStay.touchpoints.Integration.AfterCheckOut') },
        { mode: '4', name: t('flipto.account.platform.postStay.touchpoints.Integration.HostedFirstPage') },
        { mode: '0', name: t('flipto.account.platform.postStay.touchpoints.Integration.HostedFinalPage') },
      ];
      integration.isRequestPlaceForPhotoEnabled = this.parse.bool(integration.isRequestPlaceForPhotoEnabled);
      integration.isRequestPlaceForPhotoRequired = this.parse.bool(integration.isRequestPlaceForPhotoRequired);
      integration.isRequestPlaceForQuoteEnabled = this.parse.bool(integration.isRequestPlaceForQuoteEnabled);
      integration.isRequestPlaceForQuoteRequired = this.parse.bool(integration.isRequestPlaceForQuoteRequired);
      integration.allowedPlacesType = this.parse.intOrDefault(integration.allowedPlacesType);
      this.model().placesCount = this.parse.intOrDefault(integration.placesCount);
      // v1 $watch'ed isRequestPlaceForPhoto/QuoteEnabled and rebuilt these arrays IN PLACE, so the
      // "Required" row appeared/disappeared live inside the open editor (integration.controller.js:89-105).
      // Natively the row declares `showWhen` and ft-bool-field gates it off the live datasource —
      // same rendered result, and it survives the array reaching the modal as a snapshot.
      this.model().fieldsForPlacePhoto = [
        { name: 'isRequestPlaceForPhotoEnabled', label: 'Status' },
        { name: 'isRequestPlaceForPhotoRequired', label: 'Required', showWhen: 'isRequestPlaceForPhotoEnabled' },
      ];
      this.model().fieldsForPlaceQuote = [
        { name: 'isRequestPlaceForQuoteEnabled', label: 'Status' },
        { name: 'isRequestPlaceForQuoteRequired', label: 'Required', showWhen: 'isRequestPlaceForQuoteEnabled' },
      ];
      // base allowedPlacesTypes already = [{value:0,label:'All'}]; the v1 placesCount>0 watch appends "Your places (N)".
      if (this.model().placesCount > 0 && !this.model().allowedPlacesTypes.find((i: any) => i.value == 1)) {
        this.model().allowedPlacesTypes = [...this.model().allowedPlacesTypes, { value: 1, label: 'Your places (' + this.model().placesCount + ')' }];
      }
    }

    // contestPage "Story Explorer" integration (edit-contestPage-integration.html
    // `ng-init="model.permanentTransformType = 8"`): the codes/bookNow/footer commits use
    // transformType 8. Read-view fields (codes / subdomains→cnameUrl / isBookNowActive /
    // isFooterActive / companyCode) are already on model.integration (loaded generically).
    if (this.touchpointType() === 'contestPage') {
      this.model().permanentTransformType = 8;
      // WS5 #10 Stage B B0 (#7h): coerce the transform-8 envelope booleans (v1
      // integration.controller.js:163-166). isBookNowActive/isFooterActive are EXCLUDED from
      // normalizeStrings' boolProperties on BOTH hosts, so v1 added this explicit parse.bool;
      // the original native port DROPPED it → the PUT sent "1"/"0" STRINGS (not booleans) and
      // the footer modal's [checked]/*ngIf read string "0" as TRUTHY (switch rendered ON +
      // footer field shown) where v1 showed OFF/hidden. Restored for byte + render parity.
      this.model().integration.isBookNowActive = this.parse.bool(this.model().integration.isBookNowActive);
      this.model().integration.isFooterActive = this.parse.bool(this.model().integration.isFooterActive);
    }
  }

  /** homepage/list: the display name for the currently-selected profile-image type. */
  profileImageTypeName(): string {
    const id = this.model().selectedImageProfileType && this.model().selectedImageProfileType.id;
    const types = this.model().profileImageTypes || [];
    return (types[id] && types[id].name) || (types[0] && types[0].name) || '';
  }

  /** homepage/list: v1 $watch(selectedImageProfileType.id) — sync isReplacePictures + name. */
  syncProfileImageType(): void {
    const id = this.model().selectedImageProfileType && this.model().selectedImageProfileType.id;
    if (id === undefined || id === null) return;
    this.model().integration.isReplacePictures = id === 1;
    this.model().selectedImageProfileType.name = this.profileImageTypeName();
  }

  private activate(): void {
    const integration = this.integration();
    this.prepare(integration);
    this.setupVersionManager(integration);

    this.multiEngineMode ? forEach(this.bookingEngines, (e: any) => this.normalizeStrings(e)) : this.normalizeStrings(integration);

    if (this.multiEngineMode) {
      Promise.resolve(this.bookingEnginesApi.get()).then((engines: any) => {
        this.zone.run(() => {
          this.model().engines = this.parse.arrayOrDefault(engines);
          forEach(this.model().engines, (engine: any) => {
            this.model().engineNames[engine.id] = engine.name;
            this.model().instruction[engine.id] = {
              integrationCodeSnippet: engine.integrationCodeSnippet,
              integrationInstructions: engine.integrationInstructions && engine.integrationInstructions.replace('{code-snippet}', ''),
            };
          });
        });
      });
    }

    Promise.resolve(this.rateCodesApi.getRateCodes(this.spaces.getPropertyUuid())).then((data: any) => {
      this.zone.run(() => {
        this.model().rateCodes = data;
      });
    });

    this.model().language = {};
    this.model().languages = [];
    this.languageHelper.sortByDefaultLanguage(integration.languages);
    const languages = (this.model().languageItems = integration.languages ? integration.languages.item : [{ code: 'en', name: 'English' }]);
    languages &&
      languages.forEach((language: any) => {
        language.data = language.data || {};
        this.model().language[language.code] = language.data;
        this.model().language[language.code].livePreviewQueryParams = { lang: language.code, flipto: language.data.promoterCode, debug: 1 };
      });
    if (this.model().languages.length === 0 && languages) {
      languages.forEach((language: any) => this.model().languages.push({ code: language.code, name: language.name }));
    }
  }

  private normalizeStrings(source: any): void {
    const jsonProperties = ['jsonSettings'];
    const boolProperties = ['isReplacePictures', 'isPredefinedQuote', 'isLowRatingExternalReview', 'isNewQuoteProcess', 'isEnablePhotos', 'isActive', 'isDesktopReopenerActive', 'isMobileReopenerActive', 'isDesktopOpenNewWindow', 'isMobileOpenNewWindow', 'isTimerEnabled', 'isShowIncentiveOnIntro'];
    const intProperties = ['maxQuoteTextLength', 'pageSize', 'width', 'timerDuration'];
    forEach(source, (value: any, key: string) => {
      if (includes(boolProperties, key)) {
        source[key] = value === '1' ? true : false;
      }
      if (includes(intProperties, key)) {
        source[key] = parseInt(value) || 0;
      }
      if (includes(jsonProperties, key)) {
        source[key] = value == null ? '' : value.replace(/[\r]/g, '');
      }
    });
  }

  private setupVersionManager(objectToUpgrade: any): void {
    this.version = {
      currentVersion: this.versionSelection.get(this.version && this.version.currentVersion, objectToUpgrade.poststayVersionId, objectToUpgrade.installedVersionId),
      activatedVersion: objectToUpgrade.poststayVersionId,
      updateInstalled: objectToUpgrade.installedVersionId > objectToUpgrade.poststayVersionId,
      availableVersion: objectToUpgrade.lastVersionId,
      currentIsNewVersion: () => this.version && this.version.updateInstalled && this.version.currentVersion === this.version.availableVersion,
    };
  }

  private prepare(integration: any): void {
    integration.installedVersionId && (integration.installedVersionId = this.parse.intOrDefault(integration.installedVersionId));
    integration.poststayVersionId && (integration.poststayVersionId = this.parse.intOrDefault(integration.poststayVersionId));
    integration.lastVersionId && (integration.lastVersionId = this.parse.intOrDefault(integration.lastVersionId));
    integration.subdomains && (integration.subdomains = this.parse.arrayOrDefault(integration.subdomains));
    _each(integration.subdomains, (domain: any) => {
      domain.isActive = this.parse.bool(domain.isActive);
      domain.isDefault = this.parse.bool(domain.isDefault);
      domain.routePurpose = this.parse.int(domain.routePurposeId);
      domain.properties = this.parse.arrayOrDefault(domain.properties);
    });
  }

}
